import { useState } from 'react'
import { CalendarClock } from 'lucide-react'
import { useHandleSeasonalAlert } from '../../hooks/backend/inventoryPage'
import { SEASONAL_ALERTS_CHANGED, type SeasonalAlert } from '../../hooks/useHubAlerts'
import { Button } from '../../lib/shadcn/button'
import { toast } from '../../lib/shadcn/sonner'

type SeasonalAlertAction = 'publish' | 'unpublish' | 'dismiss'

const formatDay = (day: string) =>
  new Date(`${day}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })

const when = (a: SeasonalAlert) =>
  a.days_ago === 0 ? 'today' : a.days_ago === 1 ? 'yesterday' : `on ${formatDay(a.occurs_on)}`

const key = (a: SeasonalAlert) => `${a.product}|${a.event}|${a.occurs_on}`

// Seasonal products whose season started or ended, with a button to publish
// or unpublish them on Faire. An alert stays until someone acts
// on it or dismisses it, for up to two weeks. Shown in the Dashboard's Today
// section.
export default function SeasonalAlertList({ alerts }: { alerts: SeasonalAlert[] }) {
  const handleAlert = useHandleSeasonalAlert()
  const [busy, setBusy] = useState<string | null>(null)
  const [done, setDone] = useState<Set<string>>(new Set())

  const visible = alerts.filter(a => !done.has(key(a)))
  if (visible.length === 0) return null

  const act = async (a: SeasonalAlert, action: SeasonalAlertAction) => {
    setBusy(`${key(a)}|${action}`)
    try {
      await handleAlert.trigger({
        product: a.product,
        event: a.event,
        occurs_on: a.occurs_on,
        action,
      })
      setDone(prev => new Set(prev).add(key(a)))
      window.dispatchEvent(new Event(SEASONAL_ALERTS_CHANGED))
      toast.success(
        action === 'dismiss'
          ? `${a.product} alert dismissed`
          : `${a.product} ${action === 'publish' ? 'published' : 'unpublished'} on Faire`
      )
    } catch (err) {
      toast.error(
        `Couldn't ${action === 'dismiss' ? 'dismiss' : `${action} on Faire`}: ${
          err instanceof Error ? err.message : String(err)
        }`
      )
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      {visible.map(a => {
        const faireAction = a.suggested_action
        const faireLabel = faireAction === 'publish' ? 'Publish on Faire' : 'Unpublish on Faire'
        return (
          <li key={key(a)} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
            <CalendarClock className="h-4 w-4 shrink-0 text-primary" />
            <span className="flex-1 min-w-0 text-sm">
              <span className="font-medium">{a.product}</span> season{' '}
              {a.event === 'start' ? 'started' : 'ended'} {when(a)}.
            </span>
            <div className="flex items-center gap-2 max-sm:w-full max-sm:pl-7">
              <Button
                size="sm"
                variant={faireAction === 'publish' ? 'default' : 'outline'}
                className="h-8"
                disabled={busy !== null}
                onClick={() => act(a, faireAction)}
              >
                {busy === `${key(a)}|${faireAction}` ? 'Sending…' : faireLabel}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8"
                disabled={busy !== null}
                onClick={() => act(a, 'dismiss')}
              >
                Dismiss
              </Button>
            </div>
          </li>
        )
      })}
    </>
  )
}
